import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
	ENTITY_TASK_CONFIG,
	TaskQueryService,
	type EntityTasksTabProps,
	type StandardListParams,
} from '@supply-mover-2-0/core';
import type {
	RbacActionDefinition,
	TaskEntityType,
} from '@supply-mover-2-0/shared';
import { PermissionService, RbacActions } from '@supply-mover-2-0/shared';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { useTaskActions } from '../../hooks/useTaskActions';
import { ListView } from '../../list-builder';
import { wrapQueryService } from '../../list-builder/utils/wrapQueryService';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { TaskActionDialogs } from '../dialogs/TaskActionDialogs';
import { AccessDenied } from '../feedback/AccessDenied';

/**
 * Entity types that actually mount an EntityTasksTab (client, issue, license,
 * opportunity, sourcing request detail pages). 'none'/'vendor'/'external'
 * tasks have no embedded tab surface.
 */
type TabEntityType = Exclude<
	TaskEntityType,
	'external' | 'none' | 'vendor' | 'campaign_member' | 'association_membership'
>;

/**
 * Maps entity types to their RBAC read permission
 */
const ENTITY_RBAC_MAP: Record<TabEntityType, RbacActionDefinition> = {
	client: RbacActions.CLIENTS.READ,
	issue: RbacActions.ISSUES.READ,
	license: RbacActions.LICENSES.READ,
	opportunity: RbacActions.OPPORTUNITIES.READ,
	sourcing_request: RbacActions.SOURCING_REQUESTS.READ,
	trade_show: RbacActions.TRADE_SHOWS.READ,
};

/**
 * Default titles for each entity type
 */
const DEFAULT_TITLES: Record<TabEntityType, string> = {
	client: 'Client Tasks',
	issue: 'Issue Tasks',
	license: 'License Tasks',
	opportunity: 'Opportunity Tasks',
	sourcing_request: 'Sourcing Request Tasks',
	trade_show: 'To-dos and Deadlines',
};

/**
 * Error messages for each entity type
 */
const ERROR_MESSAGES: Record<TabEntityType, string> = {
	client: 'No client data available',
	issue: 'No issue data available',
	license: 'No license data available',
	opportunity: 'No opportunity data available',
	sourcing_request: 'No sourcing request data available',
	trade_show: 'No trade show data available',
};

/**
 * Generic EntityTasksTab component using the standardized ListView pattern.
 *
 * Displays tasks for any entity type with the ability to add, complete, and delete tasks.
 * This component replaces entity-specific tasks tabs (ClientTasks, IssueTasksTab,
 * LicenseTasksTab, OpportunityTasksTab, SourcingRequestTasksTab).
 */
export function EntityTasksTab({
	entityId,
	entityType,
	listKey,
	title,
	entityMeta,
	canCreate,
	onRefresh,
	showConfigButton = true,
	showFilterToggle = true,
}: EntityTasksTabProps): ReactNode {
	const { t } = useTranslation();
	const colors = useThemeColors();

	const refetchFnRef = useRef<(() => void) | null>(null);

	const typedEntityType = entityType as TabEntityType;
	const config = ENTITY_TASK_CONFIG[typedEntityType];
	const rbacPermission = ENTITY_RBAC_MAP[typedEntityType];
	const defaultTitle = DEFAULT_TITLES[typedEntityType];
	const errorMessage = ERROR_MESSAGES[typedEntityType];

	const handleRefetch = useCallback(() => {
		refetchFnRef.current?.();
		onRefresh?.();
	}, [onRefresh]);

	const taskActions = useTaskActions({
		onActionComplete: handleRefetch,
		onRefetch: handleRefetch,
	});

	const { callbacks, taskDialog } = taskActions;

	// Extend the base task callbacks with:
	//   - onRowClick → open the view modal (matches main Tasks list).
	//   - refetch → expose the list refetch so inline chip components
	//     (task-status-selector, task-priority-selector) can refresh after
	//     mutating a row.
	const listCallbacks = useMemo(
		() => ({
			...callbacks,
			onRowClick: (row: unknown) => {
				callbacks.view(row as Parameters<typeof callbacks.view>[0]);
			},
			refetch: handleRefetch,
		}),
		[callbacks, handleRefetch],
	);

	const handleRefetchReady = useCallback((refetch: () => void) => {
		refetchFnRef.current = refetch;
	}, []);

	const additionalContext = useMemo(
		() => ({
			[config.filterKey]: entityId,
			module: config.module,
		}),
		[entityId, config.filterKey, config.module],
	);

	const queryFn = useMemo(
		() =>
			wrapQueryService({
				queryFn: (queryParams: StandardListParams) => {
					const existingFilters = (queryParams.filters ?? {}) as Record<
						string,
						unknown
					>;
					const { search: filterSearch, ...restFilters } = existingFilters;
					const searchParam =
						(filterSearch as string | undefined) ?? queryParams.search;

					return TaskQueryService.getTasks({
						page: queryParams.page,
						pageSize: queryParams.pageSize,
						search: searchParam,
						sort: queryParams.sort,
						filters: {
							...restFilters,
							[config.filterKey]: entityId,
						},
						fields: queryParams.fields,
					});
				},
			}),
		[entityId, config.filterKey],
	);

	// Check entity-level read permission
	if (!PermissionService.can(rbacPermission)) {
		return <AccessDenied />;
	}

	// Handle invalid entity ID
	if (entityId == null || isNaN(entityId)) {
		return (
			<div className="p-4">
				<span className="text-sm" style={{ color: colors.values.danger }}>
					{t(errorMessage)}
				</span>
			</div>
		);
	}

	// Determine if user can create tasks
	const canCreateTask =
		canCreate ??
		(PermissionService.can(RbacActions.TASKS.CREATE_ANY) ||
			PermissionService.can(RbacActions.TASKS.CREATE_SELF));

	const headerActions = useMemo(
		() => ({
			primary: [
				{
					label: t('Add Task'),
					onClick: taskDialog.openNew,
					icon: faPlus,
					hidden: !canCreateTask,
				},
			],
		}),
		[canCreateTask, t, taskDialog.openNew],
	);

	const baseUrl = `${config.basePath}${entityId}/tasks`;

	return (
		<div className="flex flex-col h-full">
			<ListView
				variant="embedded"
				title={t(title ?? defaultTitle)}
				listKey={listKey}
				baseUrl={baseUrl}
				queryFn={queryFn}
				showConfigButton={showConfigButton}
				showFilterToggle={showFilterToggle}
				headerActions={headerActions}
				callbacks={listCallbacks}
				onRefetchReady={handleRefetchReady}
				additionalContext={additionalContext}
				autoRefreshCounts={true}
			/>

			<TaskActionDialogs
				taskActions={taskActions}
				entityContext={{
					entityType: config.entityType,
					entityId,
					entityMeta: entityMeta ?? {},
				}}
			/>
		</div>
	);
}

export default EntityTasksTab;
